import {
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Container,
  Stack,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Link as MuiLink } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { EditActivityButtonWithFetch } from '../components/EditActivityButtonWithFetch.tsx';
import { API_BASE_URL, authFetch, authFetchJson } from '../apiClient';
import { AddTaskButton } from '../components/tasks/AddTaskButton.tsx';
import { EditTaskButtonWithFetch } from '../components/tasks/EditTaskButtonWithFetch.tsx';
import {
  PipelineReport,
  PipelineReportEmailButton
} from '../components/reports/PipelineReport.tsx';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { WonReport } from '../components/reports/WonReport.tsx';
import { IntroductionsReport } from '../components/reports/IntroductionsReport.tsx';
import { LostButTrackReport } from '../components/reports/LostButTrackReport.tsx';
import PrintIcon from '@mui/icons-material/Print';
import { generateActivityTrackingPdf } from '../components/reports/generateActivityTrackingPdf.ts';
import { RenewalsPanel } from './RenewalsPanel.tsx';
import { GoneQuietPanel } from './GoneQuietPanel.tsx';

type FollowUpItem = {
  id: number;
  area: string;
  resource: string;
  resourceName: string;
  resourcePath: string;
  resourceId: number;
  note?: string | null;
  followUpDate?: string | null;
  followedUpOn?: string | null;
  activityDate?: string | null;
  keyName?: string;
};

type SummaryCounts = {
  contacts: {
    today: number;
    last7Days: number;
    last15Days: number;
    last30Days: number;
  };
  opportunitiesOpened: {
    today: number;
    last15Days: number;
    last30Days: number;
  };
  opportunitiesWon: {
    today: number;
    last15Days: number;
    last30Days: number;
  };
  events: {
    upcoming3Days: number;
    upcoming7Days: number;
    upcoming15Days: number;
    completedLast30Days: number;
  };
};

const readOnlyRecordResources = new Set([
  'contacts',
  'companies',
  'groups',
  'opportunities',
  'projects'
]);

const getActivityResourcePath = (item: FollowUpItem) => {
  const basePath = `/${item.resourcePath}/${item.resourceId}`;
  return readOnlyRecordResources.has(item.resourcePath) ? `${basePath}/show` : basePath;
};

const DashboardPage = () => {
  const [followUps, setFollowUps] = useState<FollowUpItem[]>([]);
  const [loadingFollowUps, setLoadingFollowUps] = useState(true);
  const [summary, setSummary] = useState<SummaryCounts | null>(null);
  const [hidePastDue, setHidePastDue] = useState(false);
  const [hideWithoutDueDate, setHideWithoutDueDate] = useState(false);
  const [showCleared, setShowCleared] = useState(false);

  const fetchFollowUps = useCallback(() => {
    return authFetch(`${API_BASE_URL}/dashboard/follow-ups`)
      .then((res) => res.json())
      .then((data) => {
        setFollowUps(data);
      })
      .finally(() => setLoadingFollowUps(false));
  }, []);

  const loadDashboardData = useCallback(async () => {
    await Promise.all([
      fetchFollowUps(),
      authFetchJson(`${API_BASE_URL}/dashboard/summary`)
        .then(({ json }) => setSummary(json))
        .catch(() => setSummary(null))
    ]);
  }, [fetchFollowUps]);

  const activityColumns: GridColDef[] = [
    { field: 'area', headerName: 'Area', width: 120 },
    {
      field: 'resourceName',
      headerName: 'Resource',
      width: 250,
      renderCell: (params) => {
        const item = params.row;
        if (item.area === 'tasks') {
          return <>{item.resourceName}</>;
        }

        return (
          <MuiLink component={RouterLink} to={getActivityResourcePath(item)} underline="hover">
            {item.resourceName}
          </MuiLink>
        );
      }
    },
    { field: 'note', headerName: 'Note', flex: 1 },
    {
      field: 'activityDate',
      headerName: 'Activity Date',
      width: 180,
      renderCell: (params) => {
        const rawDate = params.value;
        if (!rawDate) return '';
        const date = new Date(rawDate);
        return isNaN(date.getTime())
          ? ''
          : date.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });
      }
    },
    {
      field: 'followUpDate',
      headerName: 'Follow-Up Date',
      width: 180,
      renderCell: (params) => {
        const rawDate = params.value;
        if (!rawDate) return '';

        const [year, month, day] = rawDate.split(/[- T:]/).map(Number);
        const followUp = new Date(year, month - 1, day);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const diffInDays = Math.floor(
          (followUp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );

        let color = 'green';
        if (diffInDays < 0) color = 'red';
        else if (diffInDays <= 2) color = 'orange';

        return (
          <span style={{ color }}>
            {followUp.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </span>
        );
      }
    },
    {
      field: 'actions',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const item = params.row;
        if (item.area === 'tasks') {
          return (
            <EditTaskButtonWithFetch
              onSaved={loadDashboardData}
              recordId={item.id}
              keyName={item.keyName}
              resource={item.resource}
            />
          );
        } else {
          return (
            <EditActivityButtonWithFetch
              onSaved={loadDashboardData}
              recordId={item.id}
              keyName={item.keyName}
              resource={item.resource}
            />
          );
        }
      }
    }
  ];

  const filteredFollowUps = followUps.filter((item) => {
    const followUpDate = item.followUpDate ? new Date(item.followUpDate) : null;
    if (!showCleared && item.followedUpOn) {
      return false;
    }
    if (hideWithoutDueDate && !followUpDate) {
      return false;
    }
    if (hidePastDue && followUpDate) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dueDate = new Date(
        followUpDate.getFullYear(),
        followUpDate.getMonth(),
        followUpDate.getDate()
      );
      if (dueDate < today) {
        return false;
      }
    }
    return true;
  });

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  return (
    <Box sx={{ mt: 4 }}>
      <Container maxWidth="xl">
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 4,
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              lg: '1fr'
            }
          }}
        >
          {summary && (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))'
                }
              }}
            >
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      New Contacts
                    </Typography>
                    <MuiLink
                      component={RouterLink}
                      to="/contacts"
                      underline="hover"
                      variant="caption"
                    >
                      View all
                    </MuiLink>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',
                        sm: 'repeat(3, minmax(0, 1fr))'
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.contacts.today}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Today
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.contacts.last7Days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last 7 days
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.contacts.last15Days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last 15 days
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.contacts.last30Days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last 30 days
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Opened Opportunities
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <MuiLink
                        component={RouterLink}
                        to="/reports/pipeline"
                        underline="hover"
                        variant="caption"
                      >
                        View report
                      </MuiLink>
                      <MuiLink
                        component={RouterLink}
                        to="/opportunities"
                        underline="hover"
                        variant="caption"
                      >
                        View list
                      </MuiLink>
                    </Stack>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',
                        sm: 'repeat(3, minmax(0, 1fr))'
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.opportunitiesOpened.today}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Today
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.opportunitiesOpened.last15Days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last 15 days
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.opportunitiesOpened.last30Days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last 30 days
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Won Opportunities
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <MuiLink
                        component={RouterLink}
                        to="/reports/won"
                        underline="hover"
                        variant="caption"
                      >
                        View report
                      </MuiLink>
                      <MuiLink
                        component={RouterLink}
                        to="/opportunities"
                        underline="hover"
                        variant="caption"
                      >
                        View list
                      </MuiLink>
                    </Stack>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',
                        sm: 'repeat(4, minmax(0, 1fr))'
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.opportunitiesWon.today}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Today
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.opportunitiesWon.last15Days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last 15 days
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.opportunitiesWon.last30Days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last 30 days
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Events
                    </Typography>
                    <MuiLink
                      component={RouterLink}
                      to="/reports/events"
                      underline="hover"
                      variant="caption"
                    >
                      View all
                    </MuiLink>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',
                        sm: 'repeat(4, minmax(0, 1fr))'
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.events.upcoming3Days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Next 3 days
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.events.upcoming7Days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Next 7 days
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.events.upcoming15Days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Next 15 days
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" align="center">
                        {summary.events.completedLast30Days}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Completed (30 days)
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
          <RenewalsPanel onFollowUpLogged={fetchFollowUps} />
          <GoneQuietPanel onFollowUpLogged={fetchFollowUps} />
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Activity Tracking
              </Typography>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ mb: 2, alignItems: { xs: 'stretch', md: 'center' } }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={hidePastDue}
                      onChange={() => setHidePastDue((prev) => !prev)}
                      size="small"
                    />
                  }
                  label="Hide Past Due"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={hideWithoutDueDate}
                      onChange={() => setHideWithoutDueDate((prev) => !prev)}
                      size="small"
                    />
                  }
                  label="Hide No Due Date"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={showCleared}
                      onChange={() => setShowCleared((prev) => !prev)}
                      size="small"
                    />
                  }
                  label="Include Cleared"
                />
                <Box sx={{ flexGrow: 1 }} />
                <Box
                  sx={{
                    alignSelf: { xs: 'flex-start', md: 'center' },
                    display: 'flex',
                    gap: 1
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PrintIcon fontSize="small" />}
                    onClick={() => generateActivityTrackingPdf(filteredFollowUps)}
                  >
                    Download PDF
                  </Button>
                  <AddTaskButton
                    keyName={'taskId'}
                    resource={'tasks'}
                    onSaved={() => fetchFollowUps()}
                  />
                </Box>
              </Stack>
              {loadingFollowUps ? (
                <CircularProgress />
              ) : (
                <Box sx={{ height: 320, width: '100%' }}>
                  <DataGrid
                    rows={filteredFollowUps}
                    columns={activityColumns}
                    getRowId={(row) => `${row.area}-${row.id}`}
                    density="compact"
                    pagination
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10, page: 0 } }
                    }}
                    disableRowSelectionOnClick
                  />
                </Box>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={2}
                mb={2}
              >
                <Typography variant="h6" gutterBottom>
                  Recently Open Opportunities (L15 Days)
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PipelineReportEmailButton days="15" size="small" />
                  <Button
                    variant="outlined"
                    size="small"
                    component={RouterLink}
                    to="/reports/pipeline"
                  >
                    View Full Report
                  </Button>
                </Stack>
              </Box>
              <PipelineReport isDashboard={true} showEmailButton={false}></PipelineReport>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={2}
                mb={2}
              >
                <Typography variant="h6" gutterBottom>
                  Recently Won Opportunities (L15 Days)
                </Typography>

                <MuiLink component={RouterLink} to="/reports/won" underline="hover">
                  View Full Report
                </MuiLink>
              </Box>
              <WonReport isDashboard={true}></WonReport>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={2}
                mb={2}
              >
                <Typography variant="h6" gutterBottom>
                  Lost But Tracked Opportunities (Follow Up Within 90 Days)
                </Typography>

                <MuiLink component={RouterLink} to="/reports/lost-but-tracked" underline="hover">
                  View Full Report
                </MuiLink>
              </Box>
              <LostButTrackReport isDashboard={true}></LostButTrackReport>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={2}
                mb={2}
              >
                <Typography variant="h6" gutterBottom>
                  Recent Introductions (L60 Days)
                </Typography>
                <MuiLink component={RouterLink} to="/reports/introductions" underline="hover">
                  View Full Report
                </MuiLink>
              </Box>
              <IntroductionsReport isDashboard={true} />
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default DashboardPage;
